import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AccountSeat {
  id: string;
  workspace_id: string;
  client_id: string | null;
  status: "active" | "paused" | "expired" | "removed";
  billing_cycle: string;
  price_per_seat: number | null;
  current_period_start: string;
  current_period_end: string | null;
  auto_created: boolean;
  slug?: string | null;
  created_at: string;
}

export const CYCLE_LABELS: Record<string, string> = {
  trial: "Trial",
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half yearly",
  annual: "Annual",
  two_yearly: "Two years",
};

export function daysLeft(end: string | null | undefined): number | null {
  if (!end) return null;
  return Math.ceil((new Date(end).getTime() - Date.now()) / 86400000);
}

/** Effective state shown to users: expired if the date has passed, even before the nightly job runs. */
export function seatState(seat: Pick<AccountSeat, "status" | "current_period_end"> | undefined | null) {
  if (!seat) return { tone: "muted" as const, label: "No seat", days: null as number | null };
  const d = daysLeft(seat.current_period_end);
  if (seat.status === "paused") return { tone: "destructive" as const, label: "Paused", days: d };
  if (seat.status === "expired" || (d !== null && d < 0)) return { tone: "destructive" as const, label: "Expired", days: d };
  if (d !== null && d <= 7) return { tone: "warning" as const, label: d === 0 ? "Renews today" : `${d} day${d === 1 ? "" : "s"} left`, days: d };
  return { tone: "success" as const, label: d === null ? "Active" : `${d} days left`, days: d };
}

export const TONE_CLASSES: Record<string, string> = {
  success: "border-success/40 bg-success/10 text-success",
  warning: "border-warning/40 bg-warning/10 text-warning",
  destructive: "border-destructive/40 bg-destructive/10 text-destructive",
  muted: "border-border bg-muted text-muted-foreground",
};

export function useAccountSubscriptions(workspaceId?: string | null, enabled = true) {
  return useQuery({
    queryKey: ["account-subscriptions", workspaceId],
    enabled: !!workspaceId && enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("account_subscriptions")
        .select("id, workspace_id, client_id, status, billing_cycle, price_per_seat, current_period_start, current_period_end, auto_created, created_at, clients(linkedin_name, email)")
        .eq("workspace_id", workspaceId)
        .neq("status", "removed")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as AccountSeat[];
    },
  });
}
